import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { cn } from "@repo/utils";
import {
  anneauFocus,
  Chip,
  nomAccessibleNiveau,
  type QsortItem,
  type VarianteChip,
} from "./qsort-board-elements";

/**
 * Feuille de placement mobile, fixée en bas d'écran dès qu'un item est
 * sélectionné. Sans elle, le niveau visé se trouvait ~1 000 px sous l'item
 * touché : 23 allers-retours de scroll pour remplir la pyramide. Ici la cible
 * est toujours sous le pouce.
 *
 * Deux modes : choix du niveau, puis — si le niveau visé est plein — choix de
 * l'occupant à remplacer, comme sur desktop.
 */
export function QsortFeuilleMobile({
  item,
  niveauActuel,
  libelles,
  pyramide,
  comptes,
  niveauPlein,
  occupantsNiveauPlein,
  onNiveau,
  onRemplacer,
  onQuitterRemplacement,
  onFermer,
}: {
  item: QsortItem;
  /** Niveau où l'item est déjà placé, `null` s'il est en réserve. */
  niveauActuel: number | null;
  libelles: string[];
  pyramide: number[];
  comptes: number[];
  niveauPlein: number | null;
  occupantsNiveauPlein: QsortItem[];
  onNiveau: (niveau: number | null, parClavier: boolean) => void;
  onRemplacer: (code: string) => void;
  onQuitterRemplacement: () => void;
  onFermer: () => void;
}) {
  const nom = (niveau: number) => libelles[niveau] ?? `Niveau ${niveau + 1}`;
  const titreId = "qsort-feuille-titre";
  const titre = useRef<HTMLHeadingElement>(null);

  // Le focus suit la feuille à l'ouverture et au passage en mode remplacement :
  // au lecteur d'écran, sa question est annoncée ; au clavier, les niveaux sont
  // à une tabulation au lieu d'être en fin de page.
  useEffect(() => {
    titre.current?.focus();
  }, [item.code, niveauPlein]);

  return (
    <section
      aria-labelledby={titreId}
      className="fixed inset-x-0 bottom-0 z-40 max-h-[70vh] overflow-y-auto rounded-t-surface border-t border-charte-line-strong bg-card px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-[0_-8px_24px_-12px_rgb(43_27_47/0.25)]"
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <h2
          id={titreId}
          ref={titre}
          tabIndex={-1}
          className="pt-2 text-sm font-semibold leading-snug outline-none"
        >
          {niveauPlein === null ? (
            <>Où placer « {item.label} » ?</>
          ) : (
            <>
              {nom(niveauPlein)} est complet : quel item « {item.label} » doit-il
              remplacer ?
            </>
          )}
        </h2>
        <button
          type="button"
          onClick={onFermer}
          aria-label="Annuler la sélection"
          className={cn(
            "-mr-2 grid size-11 shrink-0 place-items-center rounded-full text-muted-foreground hover:bg-charte-hover",
            anneauFocus,
          )}
        >
          <X className="size-5" aria-hidden />
        </button>
      </div>

      {niveauPlein === null ? (
        <div className="grid grid-cols-2 gap-2">
          {pyramide.map((capacite, niveau) => {
            const ici = niveauActuel === niveau;
            const restantes = capacite - (comptes[niveau] ?? 0);
            return (
              <button
                key={niveau}
                type="button"
                disabled={ici}
                onClick={(e) => onNiveau(niveau, e.detail === 0)}
                className={cn(
                  "flex min-h-12 flex-col justify-center rounded-lg border px-3 py-1.5 text-left transition-colors",
                  anneauFocus,
                  ici
                    ? "border-charte-line bg-charte-wash text-muted-foreground"
                    : "border-charte-line-strong active:bg-charte-hover-strong",
                  // Le dernier niveau seul sur sa ligne : il prend toute la largeur.
                  pyramide.length % 2 === 1 && niveau === pyramide.length - 1 && "col-span-2",
                )}
              >
                <span className="text-sm font-semibold leading-tight">{nom(niveau)}</span>
                <span className="text-xs text-muted-foreground">
                  {ici
                    ? "Déjà ici"
                    : restantes > 0
                      ? `${restantes} place${restantes > 1 ? "s" : ""} libre${restantes > 1 ? "s" : ""}`
                      : "Complet — échanger"}
                </span>
              </button>
            );
          })}
          {niveauActuel !== null && (
            <button
              type="button"
              onClick={(e) => onNiveau(null, e.detail === 0)}
              className={cn(
                "col-span-2 min-h-11 rounded-lg px-3 text-sm font-medium text-charte-accent-light underline underline-offset-4",
                anneauFocus,
              )}
            >
              Remettre dans « À classer »
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {occupantsNiveauPlein.map((occupant) => (
            <button
              key={occupant.code}
              type="button"
              onClick={() => onRemplacer(occupant.code)}
              className={cn(
                "flex min-h-12 w-full items-center rounded-lg border border-dashed border-charte-accent bg-charte-hover-opaque px-3 text-left text-sm active:bg-charte-hover-strong",
                anneauFocus,
              )}
            >
              Remplacer « {occupant.label} »
            </button>
          ))}
          <button
            type="button"
            onClick={onQuitterRemplacement}
            className={cn(
              "min-h-11 w-full rounded-lg px-3 text-sm font-medium text-charte-accent-light underline underline-offset-4",
              anneauFocus,
            )}
          >
            Choisir un autre niveau
          </button>
        </div>
      )}
    </section>
  );
}

/**
 * Les niveaux sur mobile, tous ouverts : un accordéon obligeait à se souvenir
 * du contenu des six niveaux fermés pour comparer. Le placement, lui, se fait
 * depuis la feuille du bas — pas besoin de revenir jusqu'ici.
 */
export function QsortNiveauxMobile({
  pyramide,
  libelles,
  comptes,
  itemsDuNiveau,
  selection,
  varianteDe,
  lectureSeule,
  onTapNiveau,
  onTapItem,
}: {
  pyramide: number[];
  libelles: string[];
  comptes: number[];
  itemsDuNiveau: (niveau: number) => QsortItem[];
  selection: string | null;
  varianteDe: (code: string) => VarianteChip;
  lectureSeule: boolean;
  onTapNiveau: (niveau: number, parClavier: boolean) => void;
  onTapItem: (code: string) => void;
}) {
  return (
    <section aria-label="Votre classement" className="space-y-2">
      <h2 className="text-sm font-semibold">Votre classement</h2>
      {pyramide.map((capacite, niveau) => {
        const restantes = capacite - (comptes[niveau] ?? 0);
        return (
          <div key={niveau} className="rounded-lg border border-charte-line-strong">
            <button
              type="button"
              onClick={(e) => onTapNiveau(niveau, e.detail === 0)}
              aria-disabled={selection === null}
              aria-label={nomAccessibleNiveau(
                libelles[niveau] ?? `Niveau ${niveau + 1}`,
                comptes[niveau] ?? 0,
                capacite,
                undefined,
              )}
              className={cn(
                "flex min-h-11 w-full items-center justify-between gap-2 rounded-t-lg px-3 py-2 text-left aria-disabled:cursor-default",
                anneauFocus,
              )}
            >
              <span className="text-sm font-semibold">{libelles[niveau]}</span>
              <span
                className={cn(
                  "text-sm tabular-nums",
                  restantes === 0
                    ? "font-semibold text-charte-accent-light"
                    : "text-muted-foreground",
                )}
              >
                {comptes[niveau]}/{capacite}
              </span>
            </button>
            <div className="space-y-2 px-3 pb-3">
              {itemsDuNiveau(niveau).map((item) => (
                <Chip
                  key={item.code}
                  item={item}
                  actif={selection === item.code}
                  variante={varianteDe(item.code)}
                  lectureSeule={lectureSeule}
                  poignee={false}
                  onTap={() => onTapItem(item.code)}
                />
              ))}
              {restantes > 0 && (
                <p className="text-xs text-muted-foreground">
                  {restantes} place{restantes > 1 ? "s" : ""} libre
                  {restantes > 1 ? "s" : ""}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </section>
    );
}
